import {
  IconButton,
  TableCell,
  TableSortLabel,
  Tooltip
} from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import AddIcon from '@material-ui/icons/Add'
import DeleteIcon from '@material-ui/icons/Delete'
import PropTypes from 'prop-types'
import React from 'react'

import Button from 'app/components/generic/Button'
import { DateRangePicker } from 'app/components/generic/DateRangePicker'
import { Loading } from 'app/components/generic/LottieComponents'
import Table, { EditAction } from 'app/components/generic/Table'
import constants from 'app/constants'
import { confirm, eventAlert, eventEmitter } from 'app/helpers'

import AtHomeFormModal from './AtHomeFormModal'
import AtHomePageHeader from './AtHomePageHeader'

import { isoLocal } from '../format'

const useStyles = makeStyles((theme) => ({
  // `overflowX: hidden` + `minWidth: 0` make this root a clipping box, so its
  // min-content width is 0 and the flex <main> can shrink to the viewport
  // instead of being stretched by a wide table. The table then scrolls inside
  // its own horizontal scrollbar.
  root: { minWidth: 0, overflowX: 'hidden' },
  header: { padding: theme.spacing(1.5, 3, 0) },
  content: { padding: theme.spacing(0, 0, 3) },
  actions: { display: 'inline-flex', alignItems: 'center', gap: 4 },
  // Wide enough for "dd/MM/yyyy - dd/MM/yyyy" without the input scrolling.
  range: {
    width: 260,
    // The shared picker is sized like a full form field (56px); in this header
    // it sits beside the 36px "new …" button, so match the button (owner,
    // 2026-10-21). Scoped here so Moments/Openbook keep their size.
    '& input.custom-date-picker-input': {
      height: 36,
      padding: '6px 12px',
      fontSize: 14,
      borderRadius: 4,
      border: '1px solid rgba(0, 0, 0, 0.23)',
      boxSizing: 'border-box',
      width: '100%'
    }
  }
}))

// Column header with its own dropdown filter underneath the label.
const useHeadStyles = makeStyles((theme) => ({
  headCell: {
    position: 'sticky',
    top: 0,
    zIndex: 100,
    background: theme.palette.background.paper,
    verticalAlign: 'top',
    whiteSpace: 'nowrap'
  },
  headLabel: { fontWeight: 500, color: theme.palette.primary.main },
  headSelect: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    maxWidth: 180,
    minWidth: 90,
    font: 'inherit',
    fontSize: 12,
    color: theme.palette.text.secondary,
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: 4,
    padding: '2px 4px',
    background: theme.palette.background.paper,
    '&:focus': { outline: 'none', borderColor: theme.palette.primary.main }
  },
  headSelectActive: {
    color: theme.palette.text.primary,
    borderColor: theme.palette.primary.main
  }
}))

const baseTableOptions = {
  selectableRows: 'none',
  // Filters live in the column headers (below), so the toolbar funnel goes.
  filter: false,
  // Match the main system: the grid is end-to-end with ONE horizontal
  // scroller of its own and a fixed header. Do not wrap it in another
  // overflow container or you get two scrollbars.
  responsive: 'vertical',
  fixedHeader: true,
  tableBodyMaxHeight: 'calc(100vh - 320px)',
  print: false,
  download: true,
  viewColumns: false,
  rowsPerPage: 25,
  rowsPerPageOptions: [10, 15, 25, 100],
  elevation: 0
}

const cellText = (v) => (v === null || v === undefined ? '' : String(v))

/**
 * Column header = sortable label + a dropdown of the distinct values in that
 * column (owner's ask, 2026-10-01: "filter on each column name"). Empty value
 * = no filter. Picking a value feeds mui-datatables' own filterList, so the
 * rows, the "n of m" footer and the download all follow it.
 *
 * Exported: the Setup tabs' plain table (SetupList) wears the same header and
 * filters its own rows, so it passes no `onSort`.
 */
export const FilterHeadCell = ({
  meta,
  align,
  sortOrder,
  onSort,
  value,
  values,
  onChange
}) => {
  const classes = useHeadStyles()
  const sortable = meta.sort !== false && Boolean(onSort)
  const active = sortOrder && sortOrder.name === meta.name
  return (
    <TableCell className={classes.headCell} align={align} scope="col">
      {sortable ? (
        <TableSortLabel
          active={!!active}
          direction={active ? sortOrder.direction : 'asc'}
          onClick={onSort}
          className={classes.headLabel}
        >
          {meta.label}
        </TableSortLabel>
      ) : (
        <span className={classes.headLabel}>{meta.label}</span>
      )}
      {values && (
        <select
          aria-label={`Filter ${meta.label}`}
          className={`${classes.headSelect} ${
            value ? classes.headSelectActive : ''
          }`}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onClick={(e) => e.stopPropagation()}
        >
          <option value="">All</option>
          {values.map((v) => (
            <option key={v} value={v}>
              {v || '(blank)'}
            </option>
          ))}
        </select>
      )}
    </TableCell>
  )
}

FilterHeadCell.propTypes = {
  meta: PropTypes.object.isRequired,
  align: PropTypes.string,
  sortOrder: PropTypes.object,
  onSort: PropTypes.func,
  value: PropTypes.string.isRequired,
  values: PropTypes.array,
  onChange: PropTypes.func.isRequired
}

/**
 * The At Home list page: API-backed table with real create / edit / delete,
 * every column filterable from its own header. Each grid configures it with
 * columns + form fields + the api fns. Pass refreshKey to refetch when a
 * dependency changes.
 *
 * Owned by At Home. It began as a copy of the At Apartment grid so the two
 * programmes no longer share a file (owner, 2026-10-01); they diverge freely.
 */
const AtHomeCrudPage = ({
  title,
  tableTitle,
  entityLabel,
  fetchList,
  createItem,
  updateItem,
  deleteItem,
  columns,
  fields,
  emptyValues,
  idOf = (row) => row.uuid,
  rowToForm = (row) => row,
  // Called from the click that opens the modal (never during render), so a page
  // can prime state its fields depend on. `row` is null when creating.
  onModalOpen,
  formToPayload = (values) => values,
  refreshKey,
  headerAction,
  // Opt-in server-side date window: shows a from/to picker in the header and
  // calls fetchList({ from, to }) with it (Slots and Bookings take `from` and
  // `to`). Leave it off for a list the server does not window.
  dateRange,
  // What the empty date picker says.
  dateRangePlaceholder = 'All dates',
  rowActions,
  // Optional: clicking a row (anywhere but its action buttons) calls this with
  // the row.
  onRowClick,
  subtitle,
  deleteLabel = 'Delete',
  deleteMessage,
  // Optional: (row) => false hides Edit and Delete on that row (past slots).
  canChange = () => true,
  // Defaults to canChange; lets a page allow Edit but not Delete on a row.
  canDelete
}) => {
  const classes = useStyles()
  const [rows, setRows] = React.useState(null)
  const [modal, setModal] = React.useState({ open: false, row: null })
  // column name -> selected value ('' = all)
  const [filters, setFilters] = React.useState({})
  // The server-side window, as the API wants it: YYYY-MM-DD, '' = unbounded.
  const [range, setRange] = React.useState({ from: '', to: '' })

  const openModal = (row) => {
    if (onModalOpen) onModalOpen(row)
    setModal({ open: true, row })
  }

  const load = React.useCallback(() => {
    let active = true
    setRows(null)
    fetchList(range)
      .then((res) => active && setRows(res))
      .catch(() => active && setRows([]))
    return () => {
      active = false
    }
  }, [fetchList, range])

  // react-datepicker reports the first click of a range with no end date yet;
  // refetch once both ends are in, or the range is cleared.
  const onRangePick = (_name, _type, { startDate, endDate }) => {
    if (startDate && !endDate) return
    setRange({ from: isoLocal(startDate), to: isoLocal(endDate) })
  }

  React.useEffect(load, [load, refreshKey])

  // Distinct values per column. Columns flagged keepOrder (dates, times) keep
  // the order rows arrive in — the API sorts them — everything else is A→Z.
  // A row may offer its own filter tokens for a column in `<name>Filters`
  // (grades "1, 2, 3" → 1 / 2 / 3): then the dropdown lists the tokens and
  // `byText` says which tokens a cell stands for, for the match below.
  const distinct = React.useMemo(() => {
    const out = {}
    if (!rows) return out
    columns.forEach((col) => {
      if (col.options && col.options.filter === false) return
      const tokenKey = `${col.name}Filters`
      const tokenised = rows.some((r) => Array.isArray(r[tokenKey]))
      const byText = tokenised ? new Map() : null
      const seen = []
      rows.forEach((r) => {
        const text = cellText(r[col.name])
        const list = tokenised ? (r[tokenKey] || []).map(cellText) : [text]
        if (tokenised) byText.set(text, list)
        list.forEach((v) => {
          if (!seen.includes(v)) seen.push(v)
        })
      })
      out[col.name] = {
        values:
          col.options && col.options.keepOrder
            ? seen
            : [...seen].sort((a, b) => a.localeCompare(b)),
        byText
      }
    })
    return out
  }, [rows, columns])

  const submit = async (values) => {
    try {
      const payload = formToPayload(values)
      if (modal.row) await updateItem(idOf(modal.row), payload)
      else await createItem(payload)
      eventAlert(
        `${entityLabel} ${modal.row ? 'updated' : 'created'}`,
        constants.SUCCESS
      )
      setModal({ open: false, row: null })
      load()
    } catch (error) {
      // request.js already surfaces a toast; re-enable Save so the user can fix and retry
      eventEmitter.emit(constants.MODAL_FORM_SAVE_ERROR)
    }
  }

  // Delete always asks first (owner, 2026-10-01: a one-click delete was too
  // easy to hit by mistake). `deleteMessage` lets a page say what the delete
  // means for its rows; the default names the entity.
  const onDelete = (row) => {
    const message =
      typeof deleteMessage === 'function'
        ? deleteMessage(row)
        : deleteMessage ||
          `This ${entityLabel.toLowerCase()} will be deleted. This cannot be undone.`
    confirm(
      async () => {
        try {
          await deleteItem(idOf(row))
          eventAlert(`${entityLabel} deleted`, constants.SUCCESS)
          load()
        } catch (error) {
          // request.js already surfaces a toast
        }
      },
      message,
      undefined,
      { title: 'Are you sure?', confirmButtonText: deleteLabel }
    )
  }

  const tableColumns = columns.map((col) => {
    const value = filters[col.name] || ''
    const { values, byText } = distinct[col.name] || {}
    // Long free text (notes) wraps inside a bounded cell instead of
    // stretching the row to the width of the longest sentence.
    const wrap =
      col.options && col.options.wrap
        ? {
            customBodyRenderLite: (i) => (
              <div
                style={{ whiteSpace: 'normal', maxWidth: 360, lineHeight: 1.4 }}
              >
                {cellText(rows[i][col.name])}
              </div>
            )
          }
        : {}
    return {
      ...col,
      options: {
        ...(col.options || {}),
        ...wrap,
        filter: true,
        filterList: value ? [value] : [],
        // mui matches the whole cell; a tokenised column matches on its
        // tokens instead (its logic returns true to DROP the row).
        ...(byText
          ? {
              filterOptions: {
                logic: (cell, picked) =>
                  picked.length > 0 &&
                  !(byText.get(cellText(cell)) || []).includes(picked[0])
              }
            }
          : {}),
        customHeadRender: (meta, handleToggleColumn, sortOrder) => (
          <FilterHeadCell
            key={meta.index}
            meta={meta}
            sortOrder={sortOrder}
            onSort={() => handleToggleColumn(meta.index)}
            value={value}
            values={values}
            onChange={(v) => setFilters((f) => ({ ...f, [col.name]: v }))}
          />
        )
      }
    }
  })

  const actionCol =
    updateItem || deleteItem || rowActions
      ? [
          {
            name: '',
            label: 'Actions',
            options: {
              filter: false,
              sort: false,
              download: false,
              customBodyRenderLite: (i) => (
                // Swallow the click so Edit / Delete never also fire the row's
                // own onRowClick.
                <span
                  className={classes.actions}
                  onClick={(e) => e.stopPropagation()}
                >
                  {updateItem && canChange(rows[i]) && (
                    <EditAction
                      id={String(idOf(rows[i]))}
                      onEditClick={() => openModal(rows[i])}
                    />
                  )}
                  {deleteItem && (canDelete || canChange)(rows[i]) && (
                    <Tooltip title={deleteLabel}>
                      <IconButton onClick={() => onDelete(rows[i])}>
                        <DeleteIcon />
                      </IconButton>
                    </Tooltip>
                  )}
                  {rowActions && rowActions(rows[i], load)}
                </span>
              )
            }
          }
        ]
      : []

  const options = {
    ...baseTableOptions,
    // The chips above the table (one per active filter) can be removed from
    // there; keep our dropdowns in step with them.
    onFilterChange: (_col, filterList, type) => {
      if (type === 'reset') {
        setFilters({})
        return
      }
      setFilters(
        Object.fromEntries(
          columns.map((c, i) => [c.name, (filterList[i] || [])[0] || ''])
        )
      )
    },
    ...(onRowClick
      ? {
          onRowClick: (_, meta) => onRowClick(rows[meta.dataIndex]),
          setRowProps: () => ({ style: { cursor: 'pointer' } })
        }
      : {})
  }

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <AtHomePageHeader
          title={title}
          subtitle={subtitle}
          action={
            <>
              {dateRange && (
                <div className={classes.range}>
                  <DateRangePicker
                    name="dateRange"
                    placeholder={dateRangePlaceholder}
                    onChange={onRangePick}
                  />
                </div>
              )}
              {createItem ? (
                <Button startIcon={<AddIcon />} onClick={() => openModal(null)}>
                  New {entityLabel}
                </Button>
              ) : (
                headerAction
              )}
            </>
          }
        />
      </div>
      <div className={classes.content}>
        {rows ? (
          <Table
            data={rows}
            columns={[...tableColumns, ...actionCol]}
            options={options}
            disableFullHeight
          />
        ) : (
          <Loading />
        )}
      </div>
      {(createItem || updateItem) && (
        <AtHomeFormModal
          open={modal.open}
          title={`${modal.row ? 'Edit' : 'New'} ${entityLabel}`}
          // Fields flagged createOnly are shown only when creating; editOnly
          // ones only when editing.
          fields={(() => {
            // `fields` may be a function of the row being edited — an
            // uncontrolled autocomplete needs that row to supply its
            // `initialValue`, or editing renders an empty box and saving
            // silently clears the value.
            const resolved =
              typeof fields === 'function' ? fields(modal.row) : fields || []
            return modal.row
              ? resolved.filter((f) => !f.createOnly)
              : resolved.filter((f) => !f.editOnly)
          })()}
          initialValues={modal.row ? rowToForm(modal.row) : emptyValues}
          onSubmit={submit}
          onClose={() => setModal({ open: false, row: null })}
        />
      )}
    </div>
  )
}

AtHomeCrudPage.propTypes = {
  title: PropTypes.string,
  tableTitle: PropTypes.string,
  entityLabel: PropTypes.string.isRequired,
  fetchList: PropTypes.func.isRequired,
  createItem: PropTypes.func,
  updateItem: PropTypes.func,
  deleteItem: PropTypes.func,
  columns: PropTypes.array.isRequired,
  fields: PropTypes.oneOfType([PropTypes.array, PropTypes.func]),
  emptyValues: PropTypes.object,
  idOf: PropTypes.func,
  rowToForm: PropTypes.func,
  onModalOpen: PropTypes.func,
  formToPayload: PropTypes.func,
  refreshKey: PropTypes.any,
  headerAction: PropTypes.node,
  dateRange: PropTypes.bool,
  dateRangePlaceholder: PropTypes.string,
  rowActions: PropTypes.func,
  onRowClick: PropTypes.func,
  subtitle: PropTypes.string,
  deleteLabel: PropTypes.string,
  deleteMessage: PropTypes.oneOfType([PropTypes.string, PropTypes.func]),
  canChange: PropTypes.func,
  canDelete: PropTypes.func
}

export default AtHomeCrudPage
